/**
 * @file ClientProvider — generic React provider for any `CoreLifecycle`
 * (the workspace's typed Client) + an externally-managed `QueryClient`.
 *
 * Phase 7 of umbrella #406. Replaces the App-specific `<AppProvider
 * create={() => new MyApp({ baseUrl })}>` shape with a transport-agnostic
 * `<ClientProvider client queryClient>` whose only contract is
 * "drive the client's `configure → start` on mount and `stop` on
 * unmount; expose it through Context; render `<QueryClientProvider>`
 * around the children so TanStack hooks are reachable."
 *
 * The lifecycle ref dance from `use_service.tsx:108-144` is preserved
 * verbatim — StrictMode dev-mode double-mounts and the `INVALID_STATE`
 * guard on `configure()` interact in a subtle way (the second cycle has
 * to await the *first* cycle's promise, not retry `configure`). The
 * same machinery here means existing apps porting from `<AppProvider>`
 * to `<ClientProvider>` get identical mount-order semantics.
 */

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { CoreError, type CoreLifecycle } from '../../shared/index.ts'

////////////////////////////////////////////////////////////////////////////////
// Context
////////////////////////////////////////////////////////////////////////////////

/**
 * Internal context value. The `client` is the workspace's typed Client;
 * `running` and `error` reflect its lifecycle so descendants can render
 * loading / error surfaces if they need to (the Provider also gates
 * its own children render until `running || error`, mirroring the
 * existing `AppProvider`'s loading-gate behaviour).
 *
 * Typed as `CoreLifecycle` here — the public `useClient<C>()` hook
 * narrows back to the concrete subclass at the call site.
 */
type ClientContextValue = {
  client: CoreLifecycle
  running: boolean
  error: Error | null
}

const ClientContext = createContext<ClientContextValue | undefined>(undefined)

////////////////////////////////////////////////////////////////////////////////
// Provider
////////////////////////////////////////////////////////////////////////////////

/**
 * Props for {@link ClientProvider}.
 *
 * @typeParam C - The concrete client class (any `CoreLifecycle` subclass).
 *
 * @property client - The workspace Client instance. Constructed by the
 *   caller (typically in `main.tsx`) with its `HttpClientTransport` /
 *   `WsClientTransport` deps already wired up.
 * @property queryClient - The TanStack `QueryClient`. Pass an
 *   externally-constructed instance so route-level imperative `setQueryData`
 *   / `invalidateQueries` calls can target the same cache from
 *   client-side code.
 * @property eager - Skip the loading-gate: render the children
 *   immediately instead of holding until `start()` settles. Required
 *   for SSR / SSG-hydrated apps — server-side `renderToString` never
 *   runs the lifecycle effect, so the default gate would emit an empty
 *   body, and the client's first paint must match that body to hydrate
 *   cleanly. Only safe when descendants can render an anonymous shell
 *   from seeded / fallback cache state (the transport is usable before
 *   `start()`). Defaults to `false` (the gated behaviour admin / lab
 *   rely on).
 * @property children - Subtree that uses `useClient<C>()` and
 *   `useQuery` / friends.
 */
export type ClientProviderProps<C extends CoreLifecycle> = {
  client: C
  queryClient: QueryClient
  eager?: boolean
  children: ReactNode
}

/**
 * Generic React provider for a workspace's typed Client. Drives
 * `configure → start` on mount and `stop` on unmount; exposes the live
 * `client` through Context (read via `useClient<C>()`); renders a
 * `<QueryClientProvider>` around the children so TanStack hooks are
 * reachable everywhere underneath.
 *
 * @example
 * ```tsx
 * import { ClientProvider } from '@labcraft/core/frontend/react'
 * import { QueryClient } from '@tanstack/react-query'
 *
 * const transport = new HttpClientTransport({ baseUrl })
 * const admin = new Admin({ url: baseUrl }, { http: transport, queryClient })
 * const queryClient = new QueryClient()
 *
 * function Root() {
 *   return (
 *     <ClientProvider client={admin} queryClient={queryClient}>
 *       <Routes />
 *     </ClientProvider>
 *   )
 * }
 * ```
 */
export function ClientProvider<C extends CoreLifecycle>({
  client,
  queryClient,
  eager = false,
  children,
}: ClientProviderProps<C>): ReactNode {
  const [running, setRunning] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Cache the in-flight lifecycle promise across effect cycles. React 18+
  // StrictMode in dev fires the setup/cleanup pair twice; the client has
  // a one-shot lifecycle (configure → start) and `configure()` guards
  // `state === 'created'`, so a naive second-cycle invocation throws
  // `INVALID_STATE`. Storing the chain on a ref lets the second cycle
  // await the same promise instead of restarting the lifecycle.
  const lifecycleRef = useRef<Promise<void> | null>(null)

  useEffect(() => {
    let cancelled = false
    const svc = client // capture for closure

    if (lifecycleRef.current === null) {
      lifecycleRef.current = (async () => {
        await svc.configure()
        await svc.start()
      })()
    }

    lifecycleRef.current
      .then(() => {
        if (!cancelled) setRunning(true)
      })
      .catch(err => {
        if (!cancelled) {
          setError(err instanceof Error ? err : new Error(String(err)))
        }
      })

    return () => {
      cancelled = true
      // `stop()` is a no-op unless the service is `running`. During the
      // StrictMode synthetic cleanup the service is still `configuring`
      // or `starting`, so this is a no-op there; on the real unmount the
      // service is `running` and stop fires properly.
      svc.stop().catch(() => {
        // Ignore stop errors
      })
    }
  }, [client])

  const value: ClientContextValue = { client, running, error }

  // Hold rendering until lifecycle settles (running OR error). Consumers
  // can branch on `useClient`'s typed return for `running` vs `error` if
  // they need to render a failure surface; the gate is loading-only.
  // `eager` opts out — SSR/SSG-hydrated apps render the anonymous shell
  // immediately so server markup and the client's first paint agree.
  if (!eager && !running && !error) return null

  return (
    <ClientContext.Provider value={value}>
      <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
    </ClientContext.Provider>
  )
}

////////////////////////////////////////////////////////////////////////////////
// Hook
////////////////////////////////////////////////////////////////////////////////

/**
 * Read the workspace Client from context, narrowed to the caller's
 * concrete subclass.
 *
 * @typeParam C - The concrete client class (any `CoreLifecycle` subclass).
 *   Specify at the call site (`useClient<Admin>()`) so members reachable
 *   only on the subclass type narrow correctly without a cast.
 * @returns The live client instance.
 * @throws CoreError with code `MISSING_PROVIDER` when called outside a
 *   `<ClientProvider>`.
 */
export function useClient<C extends CoreLifecycle>(): C {
  const value = useContext(ClientContext)
  if (value === undefined) {
    throw new CoreError({
      code: 'MISSING_PROVIDER',
      message: 'useClient() must be called inside a <ClientProvider>',
    })
  }
  return value.client as C
}
